import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarCheckIcon, CheckCircleIcon, MailIcon, UsersIcon } from 'lucide-react';
import { party } from '../data/party';
type Status = 'idle' | 'submitting' | 'error';
type RsvpPayload = {
  fullName: string;
  secondGuest: string;
  guest2Email: string;
  email: string;
  phone: string;
  arrival: string;
  departure: string;
  dietary: string;
  accessibility: string;
  notes: string;
};
type RegistrationSummary = Pick<RsvpPayload, 'fullName' | 'secondGuest' | 'email' | 'guest2Email' | 'arrival' | 'departure'> & {
  registeredAt: string;
};
const RSVP_ENDPOINT = 'https://script.google.com/macros/s/AKfycbyNR8lbqq-_5Ijuyx7pjLaxmtDg61Wkv9_vNObcSP3Ui6ZLztqZptzd-E8NOq3Wjs1W/exec';
const REGISTRATION_COOKIE = 'kanchella_registration';
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;
const fieldClassName = 'w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder-white/40 outline-none transition-colors focus:border-[var(--gold)] focus:ring-2 focus:ring-[var(--gold)]/30';
export function Rsvp() {
  const [status, setStatus] = useState<Status>('idle');
  const [registration, setRegistration] = useState<RegistrationSummary | null>(() => readRegistrationCookie());
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const payload: RsvpPayload = {
      fullName: String(formData.get('fullName') ?? ''),
      secondGuest: String(formData.get('secondGuest') ?? ''),
      guest2Email: String(formData.get('guest2Email') ?? ''),
      email: String(formData.get('email') ?? ''),
      phone: String(formData.get('phone') ?? ''),
      arrival: String(formData.get('arrival') ?? ''),
      departure: String(formData.get('departure') ?? ''),
      dietary: String(formData.get('dietary') ?? ''),
      accessibility: String(formData.get('accessibility') ?? ''),
      notes: String(formData.get('notes') ?? '')
    };
    setStatus('submitting');
    try {
      await fetch(RSVP_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(payload)
      });
      const summary: RegistrationSummary = {
        fullName: payload.fullName,
        secondGuest: payload.secondGuest,
        email: payload.email,
        guest2Email: payload.guest2Email,
        arrival: payload.arrival,
        departure: payload.departure,
        registeredAt: new Date().toISOString()
      };
      saveRegistrationCookie(summary);
      setRegistration(summary);
    } catch {
      setStatus('error');
    }
  };
  const handleRegisterAnotherParty = () => {
    clearRegistrationCookie();
    setRegistration(null);
    setStatus('idle');
  };
  return <section id="rsvp" className="w-full bg-[var(--dusk)] py-24 px-5" data-id="element-76" data-mp="2ymq97mjzamsg">
      <div className="mx-auto max-w-2xl text-center" data-id="element-77" data-mp="2yo1ejqt2o2lo">
        <h2 className="font-display mb-3 text-5xl uppercase text-[var(--gold)] sm:text-7xl" data-id="element-78" data-mp="n6y67o02tyk2">
          {registration ? 'Your Registration' : 'Complete Registration'}
        </h2>
        <p className="mb-2 text-white/80" data-id="element-79" data-mp="vztekqkucae2">
          {registration ? 'Your Kanchella weekend is confirmed on this device.' : 'Reserve your place at Kanchella and share the details for your stay.'}
        </p>
        {!registration ? <p className="mb-10 text-sm text-white/55" data-id="element-80" data-mp="49f4l5fy7i7o">{party.rsvpDeadline}</p> : null}

        {registration ? <RegistrationCard registration={registration} onRegisterAnotherParty={handleRegisterAnotherParty} data-id="element-81" data-mp="3vh12sijwesh5" /> : <form onSubmit={handleSubmit} className="space-y-8 text-left" data-id="element-82" data-mp="8agv8xvsuly1">
            {status === 'error' ? <p role="alert" className="rounded-xl border border-red-300/40 bg-red-950/20 px-4 py-3 text-sm text-red-100" data-id="element-83" data-mp="1oogtm4ezy3j6">
                We couldn’t complete your registration. Please check your connection and try again.
              </p> : null}
            <fieldset className="space-y-4" data-id="element-84" data-mp="2f1n59xfyeuty">
              <legend className="font-poster mb-2 text-2xl uppercase tracking-wide text-[var(--gold)]" data-id="element-85" data-mp="2jydmwp7epyhh">Your party</legend>
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-86" data-mp="2owykshz6s62r">
                <FormField label="Full name" htmlFor="fullName" required data-id="element-87" data-mp="2tx35kk74nmys">
                  <input id="fullName" name="fullName" type="text" autoComplete="name" required placeholder="First & last name" className={fieldClassName} data-id="element-88" data-mp="2tv03v2wm45e8" />
                </FormField>
                <FormField label="Guest 2 — full name" htmlFor="secondGuest" data-id="element-89" data-mp="32w6lxgewto9l">
                  <input id="secondGuest" name="secondGuest" type="text" placeholder="First & last name" className={fieldClassName} data-id="element-90" data-mp="37pj3j6l68usu" />
                </FormField>
              </div>
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-91" data-mp="3hn75ohl3x4l1">
                <FormField label="Email address" htmlFor="email" required data-id="element-92" data-mp="3hkkso2fg5wvf">
                  <input id="email" name="email" type="email" autoComplete="email" required placeholder="you@email.com" className={fieldClassName} data-id="element-93" data-mp="3lose9qqlywpf" />
                </FormField>
                <FormField label="Guest 2 — email address" htmlFor="guest2Email" data-id="element-94" data-mp="15y92vtp76lq7">
                  <input id="guest2Email" name="guest2Email" type="email" autoComplete="email" placeholder="guest@email.com" className={fieldClassName} data-id="element-95" data-mp="10zpg5r2s55ws" />
                </FormField>
              </div>
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-96" data-mp="1tpelggmivs47">
                <FormField label="Mobile phone number" htmlFor="phone" required data-id="element-97" data-mp="1tqzge7cr1d2l">
                  <input id="phone" name="phone" type="tel" autoComplete="tel" required placeholder="(555) 555-5555" className={fieldClassName} data-id="element-98" data-mp="1oqlfd99kf3e2" />
                </FormField>
              </div>
            </fieldset>

            <fieldset className="space-y-4" data-id="element-99" data-mp="1exw5ujxkrzld">
              <legend className="font-poster mb-2 text-2xl uppercase tracking-wide text-[var(--gold)]" data-id="element-100" data-mp="2cfnt4612wc3a">Your stay</legend>
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-101" data-mp="27nw43zxkmef9">
                <FormField label="Arrival date" htmlFor="arrival" required data-id="element-102" data-mp="5rdtf26oa0j1">
                  <input id="arrival" name="arrival" type="date" required defaultValue="2026-10-02" className={fieldClassName} data-id="element-103" data-mp="5thu7b99yjf3" />
                </FormField>
                <FormField label="Departure date" htmlFor="departure" required data-id="element-104" data-mp="3s5x4oq0cozla">
                  <input id="departure" name="departure" type="date" required defaultValue="2026-10-04" className={fieldClassName} data-id="element-105" data-mp="zlzchob5co5" />
                </FormField>
              </div>
            </fieldset>

            <fieldset className="space-y-4" data-id="element-106" data-mp="3n7vkg1r6uu88">
              <legend className="font-poster mb-2 text-2xl uppercase tracking-wide text-[var(--gold)]" data-id="element-107" data-mp="3n5itx07d7bjk">Make it your weekend</legend>
              <FormField label="Dietary restrictions or food allergies" htmlFor="dietary" data-id="element-108" data-mp="3e6nmjksl3veo">
                <textarea id="dietary" name="dietary" rows={3} placeholder="Let us know how we can feed you well." className={fieldClassName} data-id="element-109" data-mp="17ghg81oltmyt" />
              </FormField>
              <FormField label="Accessibility or special accommodation requests" htmlFor="accessibility" data-id="element-110" data-mp="1cesq2cewfxh1">
                <textarea id="accessibility" name="accessibility" rows={3} placeholder="Tell us how we can best support you." className={fieldClassName} data-id="element-111" data-mp="1mhemxx0o3wje" />
              </FormField>
              <FormField label="Anything else we should know to make your weekend enjoyable?" htmlFor="notes" data-id="element-112" data-mp="1vddvzmzoulzb">
                <textarea id="notes" name="notes" rows={4} placeholder="Celebrations, preferences, questions — anything at all." className={fieldClassName} data-id="element-113" data-mp="1qiztl0mlzel5" />
              </FormField>
            </fieldset>

            <motion.button type="submit" disabled={status === 'submitting'} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="w-full rounded-full bg-[var(--gold)] py-4 font-poster text-xl uppercase tracking-[0.15em] text-[var(--night)] disabled:opacity-70" data-id="element-114" data-mp="20d097wfio284">
              {status === 'submitting' ? 'Registering…' : 'Complete Registration'}
            </motion.button>
          </form>}
      </div>
    </section>;
}
type RegistrationCardProps = {
  registration: RegistrationSummary;
  onRegisterAnotherParty: () => void;
};
function RegistrationCard({
  registration,
  onRegisterAnotherParty
}: RegistrationCardProps) {
  const guestTwo = registration.secondGuest || 'No additional guest';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="rounded-3xl border border-[var(--gold)]/40 bg-white/5 p-6 text-left text-white sm:p-10" data-id="element-115" data-mp="lh4nb7acp3g0">
      <div className="mb-7 text-center" data-id="element-116" data-mp="ui2s3rsm5ezn">
        <CheckCircleIcon className="mx-auto mb-4 h-14 w-14 text-[var(--gold)]" data-id="element-117" data-mp="uh1jrv2zf6lm" />
        <h3 className="font-poster text-3xl uppercase tracking-wide" data-id="element-118" data-mp="zh5xjdci9n85">Registration complete!</h3>
        <p className="mt-2 text-white/70" data-id="element-119" data-mp="25xrg8mhlsk21">We’ve got your details and can’t wait to host you.</p>
      </div>

      <dl className="grid gap-4 sm:grid-cols-2" data-id="element-120" data-mp="2av1g6w2mce44">
        <RegistrationDetail icon={UsersIcon} label="Your party" value={registration.fullName} data-id="element-121" data-mp="2axw08mhpzwx0" />
        <RegistrationDetail icon={UsersIcon} label="Guest 2" value={guestTwo} data-id="element-122" data-mp="2l1ua3riqjlfp" />
        <RegistrationDetail icon={MailIcon} label="Email" value={registration.email} data-id="element-123" data-mp="2g1oniprbop4h" />
        {registration.guest2Email ? <RegistrationDetail icon={MailIcon} label="Guest 2 email" value={registration.guest2Email} data-id="element-124" data-mp="2b7tw105h75uu" /> : null}
        <RegistrationDetail icon={CalendarCheckIcon} label="Arrival" value={formatDate(registration.arrival)} data-id="element-125" data-mp="229i8x83tiq75" />
        <RegistrationDetail icon={CalendarCheckIcon} label="Departure" value={formatDate(registration.departure)} data-id="element-126" data-mp="1xaou9ccsot5e" />
      </dl>

      <p className="mt-7 border-t border-white/10 pt-5 text-center text-sm text-white/55" data-id="element-127" data-mp="1scdjmzeoeem4">
        Need to change your details? Please contact Natalie at{' '}
        <a href="mailto:natalie@lacostastrategies.com" className="text-[var(--gold)] underline underline-offset-4" data-id="element-128" data-mp="3mpd5395qm31b">
          natalie@lacostastrategies.com
        </a>
        .
      </p>
      <button type="button" onClick={onRegisterAnotherParty} className="mx-auto mt-5 block text-sm font-semibold text-[var(--gold)] underline decoration-[var(--gold)]/50 underline-offset-4 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:ring-offset-2 focus:ring-offset-[var(--dusk)]" data-id="element-129" data-mp="2dq77mq3tpzw1">
        Register another party on this device
      </button>
    </motion.div>;
}
type RegistrationDetailProps = {
  icon: typeof UsersIcon;
  label: string;
  value: string;
};
function RegistrationDetail({
  icon: Icon,
  label,
  value
}: RegistrationDetailProps) {
  return <div className="rounded-xl bg-white/5 p-4" data-id="element-130" data-mp="24qaikndiuz0g">
      <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--gold)]" data-id="element-131" data-mp="24r3nrjwu1ggh">
        <Icon className="h-4 w-4" data-id="element-132" data-mp="1zx942gh891he" />
        {label}
      </dt>
      <dd className="mt-2 break-words text-sm text-white" data-id="element-133" data-mp="1c86mf8mlf8mw">{value}</dd>
    </div>;
}
type FormFieldProps = {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
};
function FormField({
  label,
  htmlFor,
  required,
  children
}: FormFieldProps) {
  return <div data-id="element-134" data-mp="3npi814fmik08">
      <label htmlFor={htmlFor} className="mb-2 block text-sm font-semibold text-white/80" data-id="element-135" data-mp="3np8ghcexk4sf">
        {label}{required ? <span aria-hidden="true" data-id="element-136" data-mp="39ywp5fq66a2c"> *</span> : null}
      </label>
      {children}
    </div>;
}
function formatDate(value: string) {
  if (!value) return 'Not provided';
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}
function readRegistrationCookie(): RegistrationSummary | null {
  if (typeof document === 'undefined') return null;
  const encodedValue = document.cookie.split('; ').find(cookie => cookie.startsWith(`${REGISTRATION_COOKIE}=`))?.split('=').slice(1).join('=');
  if (!encodedValue) return null;
  try {
    const registration = JSON.parse(decodeURIComponent(encodedValue)) as RegistrationSummary;
    return registration.fullName && registration.email ? registration : null;
  } catch {
    return null;
  }
}
function saveRegistrationCookie(registration: RegistrationSummary) {
  if (typeof document === 'undefined') return;
  document.cookie = `${REGISTRATION_COOKIE}=${encodeURIComponent(JSON.stringify(registration))}; Max-Age=${ONE_YEAR_IN_SECONDS}; Path=/; SameSite=Lax`;
}
function clearRegistrationCookie() {
  if (typeof document === 'undefined') return;
  document.cookie = `${REGISTRATION_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}